import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";
import { useNavThemeValue } from "@/components/layout/SiteShell";
import { MobileMenu } from "./MobileMenu";
import { useConfig } from "@/lib/api";

export const NAV_ITEMS = [
  { to: "/projekte", label: "Projekte", index: "01", testId: "top-nav-projects-link" },
  { to: "/leistungen", label: "Leistungen", index: "02", testId: "top-nav-services-link" },
  { to: "/unternehmen", label: "Unternehmen", index: "03", testId: "top-nav-company-link" },
  { to: "/kontakt", label: "Kontakt", index: "04", testId: "top-nav-contact-link" },
];

export const MARK_LIGHT = "/brand/la-mark-light.webp";
export const MARK_DARK = "/brand/la-mark-dark.webp";

export const Wordmark = ({ light = false, className = "" }) => (
  <span className={cn("flex items-center gap-3 leading-none", className)} data-testid="wordmark">
    <img
      src={light ? MARK_LIGHT : MARK_DARK}
      alt="L&A"
      width={791}
      height={314}
      draggable={false}
      className="block h-[24px] w-auto select-none lg:h-[26px]"
    />
    <span className={cn("mono-label hidden pt-px sm:inline", light ? "text-paper/70" : "text-ink/70")}>Projektbau</span>
  </span>
);

export const TopNav = () => {
  const theme = useNavThemeValue();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const { data: cfg } = useConfig();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = theme === "light" && !scrolled;

  return (
    <header
      data-testid="top-nav"
      className={cn(
        "fixed inset-x-0 top-0 z-[60] transition-[background-color,border-color,backdrop-filter] duration-500 ease-arch",
        scrolled ? "bg-paper/90 backdrop-blur-[3px] border-b hl" : "border-b border-transparent",
      )}
    >
      <motion.div
        aria-hidden="true"
        className={cn("absolute left-0 top-0 h-px w-full origin-left", light ? "bg-paper/70" : "bg-ink")}
        style={{ scaleX: progress }}
      />
      <div className="container-x flex h-[72px] items-center justify-between lg:h-[84px]">
        <Link to="/" aria-label="L&A Projektbau – Startseite" data-testid="top-nav-logo-link" className="relative z-10">
          <Wordmark light={light} />
        </Link>

        <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={cn(
                  "group relative link-line font-heading text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300",
                  light ? "text-paper" : "text-ink",
                )}
                aria-current={active ? "page" : undefined}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute -left-5 top-[1px] mono-label text-[9px] opacity-0 -translate-x-1 transition-[opacity,transform] duration-300 ease-arch group-hover:opacity-60 group-hover:translate-x-0",
                    light ? "text-paper" : "text-ink",
                  )}
                >
                  {item.index}
                </span>
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/kontakt"
            data-testid="top-nav-project-request-button"
            className={cn(
              "hidden md:inline-flex h-10 items-center border px-5 font-heading text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ease-arch",
              light
                ? "border-paper/40 text-paper hover:bg-paper hover:text-ink"
                : "border-ink/30 text-ink hover:bg-ink hover:text-paper",
            )}
          >
            Projekt anfragen
          </Link>
          <MobileMenu light={light} cfg={cfg} />
        </div>
      </div>
    </header>
  );
};
